// src/hooks/useSpeech.js
import { useState, useRef, useCallback, useEffect } from "react";
import { cleanForSpeech, splitIntoChunks } from "../utils/cleanForSpeech";

// ── Detect language from text ─────────────────────────────────────
function detectLanguage(text) {
  if (!text) return "en";
  const sample = text.slice(0, 300);
  const counts = {
    hi: (sample.match(/[\u0900-\u097F]/g) || []).length,
    bn: (sample.match(/[\u0980-\u09FF]/g) || []).length,
    ta: (sample.match(/[\u0B80-\u0BFF]/g) || []).length,
    te: (sample.match(/[\u0C00-\u0C7F]/g) || []).length,
    kn: (sample.match(/[\u0C80-\u0CFF]/g) || []).length,
    ml: (sample.match(/[\u0D00-\u0D7F]/g) || []).length,
    gu: (sample.match(/[\u0A80-\u0AFF]/g) || []).length,
    pa: (sample.match(/[\u0A00-\u0A7F]/g) || []).length,
  };
  const maxLang = Object.entries(counts).reduce(
    (best, [lang, count]) => (count > best.count ? { lang, count } : best),
    { lang: "en", count: 4 }
  );
  return maxLang.lang;
}

const VOICE_MAP = {
  hi: ["Google हिन्दी", "Google Hindi", "Microsoft Swara"],
  bn: ["Google বাংলা", "Google Bengali", "Microsoft Bashkar"],
  ta: ["Google தமிழ்", "Google Tamil"],
  te: ["Google తెలుగు", "Google Telugu"],
  kn: ["Google ಕನ್ನಡ", "Google Kannada"],
  ml: ["Google മലയാളം", "Google Malayalam"],
  gu: ["Google ગુજરાતી", "Google Gujarati"],
  pa: ["Google ਪੰਜਾਬੀ", "Google Punjabi"],
  en: [
    "Microsoft Heera - English (India)",
    "Microsoft Ravi - English (India)",
    "Google UK English Female",
    "Google US English",
  ],
};

const LANG_CODE_MAP = {
  hi: "hi-IN", bn: "bn-IN", ta: "ta-IN", te: "te-IN",
  kn: "kn-IN", ml: "ml-IN", gu: "gu-IN", pa: "pa-IN", en: "en-IN",
};

// ── FIX 1: Check if a voice actually exists for this language ─────
// If no native voice found, fall back to English voice + English lang
// This prevents the "skip" bug on desktop where bn-IN voice missing
function resolveVoiceAndLang(detectedLang) {
  const voices    = window.speechSynthesis.getVoices();
  const preferred = VOICE_MAP[detectedLang] || VOICE_MAP["en"];
  const bcp       = LANG_CODE_MAP[detectedLang];

  // Try preferred voice names first
  for (const name of preferred) {
    const found = voices.find((v) => v.name === name);
    if (found) return { voice: found, lang: bcp };
  }

  // Try any voice with matching lang code
  const langMatch = voices.find((v) => v.lang === bcp);
  if (langMatch) return { voice: langMatch, lang: bcp };

  // ── FIX 1: No native voice found ──────────────────────────────
  // Do NOT set lang to bn-IN if no bn voice exists — Chrome will skip
  // Instead: use best English voice with en-IN lang
  const enPreferred = VOICE_MAP["en"];
  for (const name of enPreferred) {
    const found = voices.find((v) => v.name === name);
    if (found) return { voice: found, lang: "en-IN" };
  }

  // Last resort: any en-IN voice
  const enIn = voices.find((v) => v.lang === "en-IN");
  if (enIn) return { voice: enIn, lang: "en-IN" };

  // Absolute last resort: let browser decide
  return { voice: null, lang: "en-IN" };
}

export default function useSpeech(rawText) {
  const [isPlaying,   setIsPlaying]   = useState(false);
  const [isPaused,    setIsPaused]    = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [progress,    setProgress]    = useState(0);
  const [rate,        setRate]        = useState(0.85);

  // ── FIX 2: ALL mutable values in refs — no stale closure issues ──
  const rateRef         = useRef(0.85);
  const chunksRef       = useRef([]);
  const chunkIndexRef   = useRef(0);
  const detectedLangRef = useRef("en");
  const isStoppedRef    = useRef(false);
  const isPlayingRef    = useRef(false); // ← FIX 2: track isPlaying in ref

  useEffect(() => {
    if (!("speechSynthesis" in window)) setIsSupported(false);
  }, []);

  useEffect(() => {
    return () => window.speechSynthesis?.cancel();
  }, []);

  // ── Core speak function — reads from refs, no stale closures ─────
  const speakChunk = useCallback((index, chunks, detectedLang) => {
    if (isStoppedRef.current) return;

    if (index >= chunks.length) {
      setIsPlaying(false);
      setIsPaused(false);
      setProgress(100);
      isPlayingRef.current  = false;
      chunkIndexRef.current = 0;
      return;
    }

    const utterance       = new SpeechSynthesisUtterance(chunks[index]);
    const { voice, lang } = resolveVoiceAndLang(detectedLang);

    if (voice) utterance.voice = voice;

    // ── FIX 1: Only set lang if voice supports it ─────────────────
    // If voice is English but text is Bengali, set lang to en-IN
    // so Chrome doesn't mismatch and skip
    utterance.lang   = lang;
    utterance.rate   = rateRef.current; // ← FIX 2: always latest rate
    utterance.pitch  = 1.0;
    utterance.volume = 1.0;

    utterance.onstart = () => {
      setProgress(Math.round((index / chunks.length) * 100));
    };

    utterance.onend = () => {
      if (isStoppedRef.current) return;
      chunkIndexRef.current = index + 1;
      speakChunk(index + 1, chunks, detectedLang);
    };

    utterance.onerror = (e) => {
      if (e.error === "interrupted" || e.error === "canceled") return;
      if (isStoppedRef.current) return;
      console.warn("TTS error chunk", index, e.error);
      chunkIndexRef.current = index + 1;
      // Small delay before next chunk on error
      setTimeout(() => speakChunk(index + 1, chunks, detectedLang), 200);
    };

    window.speechSynthesis.speak(utterance);
  }, []); // ← No dependencies — reads everything from refs

  // ── PUBLIC: play ─────────────────────────────────────────────────
  const play = useCallback(() => {
    if (!isSupported || !rawText) return;

    if (isPaused) {
      // Mobile-safe resume: restart from saved chunk index
      isStoppedRef.current = false;
      window.speechSynthesis.cancel();

      const chunks       = chunksRef.current;
      const resumeIndex  = chunkIndexRef.current;
      const detectedLang = detectedLangRef.current;

      setIsPlaying(true);
      setIsPaused(false);
      isPlayingRef.current = true;

      setTimeout(() => {
        if (!isStoppedRef.current) speakChunk(resumeIndex, chunks, detectedLang);
      }, 150);
      return;
    }

    // Fresh start
    isStoppedRef.current = false;
    window.speechSynthesis.cancel();

    const cleaned      = cleanForSpeech(rawText);
    const chunks       = splitIntoChunks(cleaned);
    const detectedLang = detectLanguage(cleaned);

    chunksRef.current       = chunks;
    chunkIndexRef.current   = 0;
    detectedLangRef.current = detectedLang;
    isPlayingRef.current    = true;

    setIsPlaying(true);
    setIsPaused(false);
    setProgress(0);

    const startSpeaking = () => {
      if (!isStoppedRef.current) speakChunk(0, chunks, detectedLang);
    };

    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.onvoiceschanged = null;
        startSpeaking();
      };
    } else {
      startSpeaking();
    }
  }, [isSupported, rawText, isPaused, speakChunk]);

  // ── PUBLIC: pause ────────────────────────────────────────────────
  const pause = useCallback(() => {
    if (!isSupported) return;
    isStoppedRef.current = true;
    isPlayingRef.current = false;
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(true);
  }, [isSupported]);

  // ── PUBLIC: stop ─────────────────────────────────────────────────
  const stop = useCallback(() => {
    if (!isSupported) return;
    isStoppedRef.current  = true;
    isPlayingRef.current  = false;
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    setProgress(0);
    chunkIndexRef.current = 0;
  }, [isSupported]);

  // ── PUBLIC: change speed ─────────────────────────────────────────
  const changeRate = useCallback((newRate) => {
    // ── FIX 2: Update ref FIRST — speakChunk reads from ref ───────
    rateRef.current = newRate;
    setRate(newRate);

    // ── FIX 2: Use ref to check isPlaying — no stale closure ──────
    if (isPlayingRef.current) {
      isStoppedRef.current = false;
      window.speechSynthesis.cancel();

      const chunks       = chunksRef.current;
      const idx          = chunkIndexRef.current;
      const detectedLang = detectedLangRef.current;

      setTimeout(() => {
        if (!isStoppedRef.current) speakChunk(idx, chunks, detectedLang);
      }, 150);
    }
  }, [speakChunk]); // ← isPlaying removed — using ref now

  return {
    isPlaying,
    isPaused,
    isSupported,
    progress,
    rate,
    play,
    pause,
    stop,
    changeRate,
  };
}